import React, { useEffect, useRef, useState } from 'react';
import { Mail, Phone, MapPin, Calendar, MessageSquare, Send, ArrowRight, Sun, Moon, Sparkles, Github, Linkedin, Clock, CheckCircle, Globe, Video, Users, Zap } from 'lucide-react';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import { useTheme } from './src/ThemeContext.jsx';

/**
 * Schedule a Call — Standalone Page (Advanced 3D/hover UX)
 * - Matches ContactPage UI/UX with same navbar and theming
 * - 3D tilt + glow effects tuned for BOTH light and dark modes
 * - Parallax sparkles, magnetic buttons; respects reduced motion
 */

export default function ScheduleCallPage() {
  const { darkMode, toggleTheme } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    callType: '',
    phoneNumber: '',
    preferredDate: '',
    preferredTime: '',
    timezone: '',
    projectDetails: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', email: '', company: '', callType: '', phoneNumber: '', preferredDate: '', preferredTime: '', timezone: '', projectDetails: '' });
    }, 3000);
  };

  return (
    <div className={darkMode ? 'min-h-screen bg-[#0a0c10] text-white' : 'min-h-screen bg-white text-gray-900'}>
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[60] px-3 py-2 rounded-md bg-indigo-600 text-white">Skip to content</a>

      {/* NAV — same as ContactPage */}
      <header className="sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto mt-4 px-4 sm:px-6">
          <div className={`backdrop-blur rounded-2xl shadow-sm flex items-center justify-between px-4 sm:px-6 py-3 ${darkMode ? 'bg-white/5 border border-white/10' : 'bg-white/80 border border-white/90'}`}>
            <a href="/" className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-semibold text-white ${darkMode ? 'bg-gradient-to-br from-indigo-500 to-cyan-400' : 'bg-gradient-to-br from-indigo-600 to-cyan-500 drop-shadow'}`}>ST</div>
              <div className="leading-tight">
                <div className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Sikandar Tareen</div>
                <div className={`text-[11px] ${darkMode ? 'text-white/60' : 'text-gray-600'}`}>Agentic AI Engineer</div>
              </div>
            </a>
            <div className={`hidden md:flex items-center gap-6 text-sm ${darkMode ? 'text-white/70' : 'text-gray-700'}`}>
              <a href="/" className={`${darkMode ? 'hover:text-white' : 'hover:text-black'}`}>Home</a>
              <a href="/#projects" className={`${darkMode ? 'hover:text-white' : 'hover:text-black'}`}>Projects</a>
              <a href="/blog" className={`${darkMode ? 'hover:text-white' : 'hover:text-black'}`}>Blog</a>
              <a href="/about-me" className={`${darkMode ? 'hover:text-white' : 'hover:text-black'}`}>About</a>
              <a href="/contact" className={`${darkMode ? 'hover:text-white' : 'hover:text-black'}`}>Contact</a>
            </div>
          </div>
        </nav>
      </header>

      {/* PARALLAX SPARKLES */}
      <ParallaxField darkMode={darkMode} />

      <main id="main" className="min-h-screen px-4 sm:px-6 pb-20 pt-10 [perspective:1200px]">
        {/* Hero */}
        <section className="relative overflow-visible text-center mb-8">
          <div className={`absolute -top-24 -left-24 w-[42rem] h-[42rem] rounded-full blur-3xl ${darkMode ? 'bg-indigo-600/20' : 'bg-indigo-200/60'}`}></div>
          <div className={`absolute -bottom-32 -right-16 w-[36rem] h-[36rem] rounded-full blur-3xl ${darkMode ? 'bg-cyan-500/20' : 'bg-cyan-200/60'}`}></div>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="relative z-10">
            <h1 className="text-4xl sm:text-5xl font-extrabold">Schedule a Call</h1>
            <p className={`mt-3 text-lg ${darkMode ? 'text-white/80' : 'text-gray-700'}`}></p>
          </motion.div>
        </section>

        {/* Schedule Form */}
        <section id="schedule-form" className="flex-1">
          <div className="max-w-4xl mx-auto">
            {/* Form */}
            <TiltCard darkMode={darkMode} glow className={`${darkMode ? 'bg-white/5 border-white/10 text-white hover:shadow-cyan-900/30' : 'bg-white border-gray-200 text-gray-900 hover:shadow-indigo-200'} rounded-2xl p-8 md:p-12 border min-h-[70vh]`}>

              
              {isSubmitted ? (
                <div className="text-center py-8">
                  <CheckCircle className="mx-auto mb-4 text-green-500" size={48} />
                  <h4 className="text-lg font-semibold mb-2">Call Scheduled!</h4>
                  <p className={`${darkMode ? 'text-white/70' : 'text-gray-600'}`}>Thank you for booking a call. You'll receive a calendar invitation shortly with the meeting details.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-white/5 border-white/10 text-white placeholder-white/50' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-white/5 border-white/10 text-white placeholder-white/50' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Company</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-white/5 border-white/10 text-white placeholder-white/50' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                        placeholder="Your company"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Call Type *</label>
                      <select
                        name="callType"
                        value={formData.callType}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      >
                        <option value="">Select call type</option>
                        <option value="phone">Phone</option>
                        <option value="google-meet">Google Meet</option>
                        <option value="zoom">Zoom</option>
                      </select>
                    </div>
                  </div>

                  {formData.callType === 'phone' && (
                    <div>
                      <label className="block text-sm font-medium mb-2">Phone Number *</label>
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-white/5 border-white/10 text-white placeholder-white/50' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                        placeholder="+1 (000) 000-0000"
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Preferred Date *</label>
                      <input
                        type="date"
                        name="preferredDate"
                        value={formData.preferredDate}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Preferred Time *</label>
                      <input
                        type="time"
                        name="preferredTime"
                        value={formData.preferredTime}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Timezone *</label>
                      <select
                        name="timezone"
                        value={formData.timezone}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      >
                        <option value="">Select timezone</option>
                        <option value="EST">Eastern (EST)</option>
                        <option value="CST">Central (CST)</option>
                        <option value="MST">Mountain (MST)</option>
                        <option value="PST">Pacific (PST)</option>
                        <option value="UTC">UTC</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Project Details</label>
                    <textarea
                      name="projectDetails"
                      value={formData.projectDetails}
                      onChange={handleInputChange}
                      rows={5}
                      className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-white/5 border-white/10 text-white placeholder-white/50' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none`}
                      placeholder="Tell me about your project, goals, and what you'd like to discuss during our call..."
                    />
                  </div>
                  
                  <Magnetic>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full px-6 py-3 rounded-lg bg-blue-600 text-white font-medium inline-flex items-center justify-center gap-2 hover:opacity-95 transition focus:outline-none focus:ring-2 ${darkMode ? 'focus:ring-cyan-300/60' : 'focus:ring-indigo-500'} disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          Scheduling...
                        </>
                      ) : (
                        <>
                          Schedule Call <Calendar size={16} />
                        </>
                      )}
                    </button>
                  </Magnetic>
                </form>
              )}
            </TiltCard>
          </div>
        </section>
      </main>

      <footer className={`py-10 text-sm ${darkMode ? 'text-white/60' : 'text-gray-600'} border-t ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">© {new Date().getFullYear()} Sikandar Tareen</div>
      </footer>

      {/* Theme toggle */}
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={toggleTheme}
          className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-lg border transition focus:outline-none focus:ring-2 ${darkMode ? 'border-white/20 bg-[#1a1c20] text-white hover:opacity-90 focus:ring-cyan-300/60' : 'border-gray-300 bg-white/95 backdrop-blur text-gray-800 hover:opacity-90 focus:ring-indigo-500'}`}
          aria-pressed={darkMode}
          aria-label="Toggle theme"
        >
          {darkMode ? <Sun size={16} /> : <Moon size={16} />}
          {darkMode ? 'Light Mode' : 'Dark Mode'}
        </button>
      </div>

      {/* Reduce motion support */}
      <style>{`
        @media (prefers-reduced-motion: reduce) {
          * { animation: none !important; transition: none !important; }
        }
      `}</style>
    </div>
  );
}

function Chip({ children, darkMode }) {
  return (
    <div className={`${darkMode ? 'bg-white/10 border-white/15 text-white' : 'bg-white border-gray-200 text-gray-900'} inline-flex items-center gap-2 px-3 py-1.5 rounded-full border`}>{children}</div>
  );
}

/** TiltCard: 3D tilt on hover with theme-aware glow */
function TiltCard({ children, className = '', glow = false, darkMode = false }) {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const tx = useSpring(rx, { stiffness: 150, damping: 15 });
  const ty = useSpring(ry, { stiffness: 150, damping: 15 });

  function onMove(e) {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width; // 0..1
    const py = (e.clientY - rect.top) / rect.height; // 0..1
    const ROT = 8; // degrees
    rx.set((0.5 - py) * ROT);
    ry.set((px - 0.5) * ROT);
    // propagate mouse position to CSS vars for glow
    el.style.setProperty('--mx', `${Math.round(px * 100)}%`);
    el.style.setProperty('--my', `${Math.round(py * 100)}%`);
  }
  function onLeave() { rx.set(0); ry.set(0); }

  const glowColor = darkMode ? 'rgba(34,211,238,0.28)' : 'rgba(99,102,241,0.25)';

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateX: tx, rotateY: ty, transformStyle: 'preserve-3d' }}
      className={`group relative will-change-transform transition-shadow hover:-translate-y-0.5 ${className}`}
    >
      {glow && (
        <div className="pointer-events-none absolute inset-0 rounded-[inherit] blur-2xl opacity-0 group-hover:opacity-40 transition-opacity" style={{
          background: `radial-gradient(520px circle at var(--mx,50%) var(--my,50%), ${glowColor}, transparent 45%)`
        }} />
      )}
      {children}
    </motion.div>
  );
}

/** Magnetic: slight translation toward cursor on hover */
function Magnetic({ children }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 120, damping: 10 });
  const sy = useSpring(y, { stiffness: 120, damping: 10 });

  function onMove(e) {
    const el = ref.current; if (!el) return;
    const rect = el.getBoundingClientRect();
    x.set(((e.clientX - rect.left) / rect.width - 0.5) * 10);
    y.set(((e.clientY - rect.top) / rect.height - 0.5) * 10);
  }
  function onLeave() { x.set(0); y.set(0); }

  return (
    <motion.div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} style={{ x: sx, y: sy }} className="inline-block">
      {children}
    </motion.div>
  );
}

/** Parallax field: subtle floating sparkles */
function ParallaxField({ darkMode }) {
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <div className={`absolute left-1/4 top-10 w-40 h-40 rounded-full ${darkMode ? 'bg-white/5' : 'bg-indigo-100'} blur-3xl animate-[float_10s_ease-in-out_infinite]`} />
      <div className={`absolute right-10 top-1/3 w-24 h-24 rounded-full ${darkMode ? 'bg-indigo-600/20' : 'bg-indigo-200/70'} blur-2xl animate-[float_12s_ease-in-out_infinite]`} />
      <div className={`absolute left-10 bottom-10 w-24 h-24 rounded-full ${darkMode ? 'bg-cyan-500/20' : 'bg-cyan-200/70'} blur-2xl animate-[float_14s_ease-in-out_infinite]`} />
      <style>{`@keyframes float { 0%,100%{ transform: translateY(0)} 50%{ transform: translateY(-12px)} }`}</style>
    </div>
  );
}